// apps/web/src/features/notifications/activeContext.ts
//
// @aiPurpose tracks the currently-active notification target/tab(s) so
//   the realtime handler can immediately clear a matching incoming
//   aggregate (#1072 active-context invariant). The drawer's
//   `useAutoMarkNotificationsViewed` effect publishes the active set
//   on mount/tab-change; the realtime bootstrap reads it on every
//   `notifications:updated` event to optimistically zero the cache
//   slot before the row badge can flash.
// @aiInvariant module-level singleton intentionally — the realtime
//   handler is wired once at bootstrap and must observe the latest
//   active set without a React re-bind on every tab switch. Subscribe
//   semantics let the drawer hook keep its own life-cycle without
//   leaking React state into the realtime layer.

import type { NotificationEntityType, NotificationType } from '@magimp/shared';

export interface ActiveTarget {
  entityType: NotificationEntityType;
  entityId: string;
  // INVARIANT: every notification type the user is currently viewing
  //   for this target. `responsibleStatus` is added by drawer mount;
  //   tab activation adds the routing-tab fan-out (carriers ⇒ {rate,
  //   rejection, insurance}, chat ⇒ {chat}).
  tabsViewing: ReadonlySet<NotificationType>;
}

type Listener = (target: ActiveTarget | null) => void;

let current: ActiveTarget | null = null;
const listeners = new Set<Listener>();

export function getActiveNotificationTarget(): ActiveTarget | null {
  return current;
}

export function setActiveNotificationTarget(next: ActiveTarget | null): void {
  current = next;
  for (const l of listeners) l(next);
}

export function subscribeActiveNotificationTarget(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/**
 * @aiPurpose predicate consumed by the realtime handler: does the
 *   incoming `notifications:updated` event target a slot the user is
 *   actively viewing?
 */
export function isActiveTargetMatch(
  target: ActiveTarget | null,
  evt: {
    entityType: NotificationEntityType;
    entityId: string;
    notificationType: NotificationType;
  },
): boolean {
  if (!target) return false;
  if (target.entityType !== evt.entityType) return false;
  if (target.entityId !== evt.entityId) return false;
  return target.tabsViewing.has(evt.notificationType);
}
